import React, { useState, useEffect } from "react";
import { Plus, Edit, Trash2, CheckCircle, X, Shield, Copy } from "lucide-react";
import api from "../utils/api";
import DeleteConfirmModal from "../components/DeleteConfirmModal";
import { useToast } from "../context/ToastContext";
import PageHeader from "../components/PageHeader";

const Agents = ({ agents, fetchAgents, setActiveAgentId }) => {
    const { showToast } = useToast();
    const [isLoading, setIsLoading] = useState(false);

    // Modal state (Create/Edit)
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingAgent, setEditingAgent] = useState(null);
    const [formData, setFormData] = useState({ name: "", description: "", is_active: true });

    // Delete confirmation state
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [agentToDelete, setAgentToDelete] = useState(null);

    // Billing & usage limits state
    const [billingInfo, setBillingInfo] = useState({
        maxAgents: null,
        usedUserSlots: 0,
        maxUserSlots: null,
        planName: "Starter"
    });

    // Initial fetch if agents are empty (safety)
    useEffect(() => {
        if (!agents || agents.length === 0) {
            fetchAgents();
        }
    }, [agents, fetchAgents]);

    useEffect(() => {
        const loadBillingInfo = async () => {
            try {
                const userStr = sessionStorage.getItem("user");
                const user = userStr ? JSON.parse(userStr) : null;
                const tenantId = user?.tenant_id || "admin";

                // Fetch subscription details
                const subRes = await api.request(`/api/subscription/${tenantId}`);
                let maxAgents = 0; // Default fallback to 0 when no plan is active
                let planName = "No Plan";
                if (subRes && subRes.success && subRes.data) {
                    const planId = subRes.data.plan_id.toLowerCase();
                    planName = subRes.data.plan_name;
                    const PLAN_PRESETS = {
                        free_trial: 1,
                        starter: 3,
                        growth: 5,
                        professional: 7,
                        enterprise: 999
                    };
                    maxAgents = PLAN_PRESETS[planId] || 0;
                }

                // Fetch human user slots
                const slotRes = await api.request(`/api/user-slots/${tenantId}`);
                let usedUserSlots = 0;
                let maxUserSlots = 0; // Default fallback to 0 when no plan is active
                if (slotRes && slotRes.success) {
                    usedUserSlots = slotRes.usedSlots || slotRes.data?.length || 0;
                    maxUserSlots = slotRes.totalSlots || 0;
                }

                setBillingInfo({
                    maxAgents,
                    usedUserSlots,
                    maxUserSlots,
                    planName
                });
            } catch (err) {
                console.error("Failed to load billing limits for Agents page:", err);
            }
        };

        loadBillingInfo();
    }, []);



    const handleOpenModal = (agent = null) => {
        if (agent) {
            setEditingAgent(agent);
            setFormData({ name: agent.name, description: agent.description || "", is_active: agent.is_active });
        } else {
            const limitReached = billingInfo.maxAgents !== null && (agents ? agents.length : 0) >= billingInfo.maxAgents;
            if (limitReached) {
                showToast(`You have reached the maximum limit of ${billingInfo.maxAgents} AI Agent slots on your current plan. Please upgrade to unlock more slots.`, "error");
                return;
            }
            setEditingAgent(null);
            setFormData({ name: "", description: "", is_active: true });
        }
        setIsModalOpen(true);
    };

    const handleOpenDeleteModal = (agent) => {
        setAgentToDelete(agent);
        setIsDeleteModalOpen(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Local Validation: Case-insensitive duplicate check
        const normalizedName = formData.name.trim().toLowerCase();
        const isDuplicate = agents.some(p =>
            p.name.trim().toLowerCase() === normalizedName &&
            (!editingAgent || p.id !== editingAgent.id)
        );

        if (isDuplicate) {
            showToast(`A bot named "${formData.name}" already exists.`, "error");
            return;
        }

        try {
            if (editingAgent) {
                // Update
                await api.request(`/agents/${editingAgent.id}`, {
                    method: "PUT",
                    body: JSON.stringify(formData),
                });
                showToast("Agent updated successfully");
            } else {
                // Create
                const response = await api.request("/agents", {
                    method: "POST",
                    body: JSON.stringify({ name: formData.name, description: formData.description }),
                });
                showToast("Agent created successfully");
            }
            setIsModalOpen(false);
            // Trigger global refresh
            fetchAgents();
        } catch (error) {
            console.error("Error saving agent:", error);
            const detail = error?.response?.detail || "Failed to save agent";
            showToast(detail, "error");
        }
    };

    const handleDelete = async () => {
        if (!agentToDelete) return;

        setIsLoading(true);
        try {
            await api.request(`/agents/${agentToDelete.id}`, {
                method: "DELETE",
            });
            showToast("Agent and all associated data deleted successfully");
            setIsDeleteModalOpen(false);
            setAgentToDelete(null);
            // Trigger global refresh
            fetchAgents();
        } catch (error) {
            console.error("Error deleting agent:", error);
            showToast("Failed to delete agent", "error");
        } finally {
            setIsLoading(false);
        }
    };

    const userStr = sessionStorage.getItem("user");
    const user = userStr ? JSON.parse(userStr) : null;

    return (
        <div className="min-h-screen bg-[#FDFCFD] pt-4 pb-8 px-8 lg:pt-6 lg:pb-12 lg:px-12 animate-fadeIn">
            <div className="max-w-6xl mx-auto">
                <PageHeader
                    title="Agents"
                    description="Create and manage agents with isolated knowledge bases, tools, and configuration settings."
                    actions={
                        <button
                            onClick={() => handleOpenModal()}
                            className="flex items-center justify-center gap-2 bg-gray-900 text-white px-8 py-2.5 rounded-xl hover:bg-black transition-all font-black text-sm uppercase shadow-xl shadow-gray-200 active:scale-95 border border-transparent"
                        >
                            <Plus size={18} />
                            Create New Agent
                        </button>
                    }
                />

                {/* Limits & Usage Banner */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8 animate-slideIn">
                    {/* AI Agents Usage */}
                    <div className="bg-white rounded-[24px] p-6 border border-gray-100 shadow-md shadow-gray-100/30 flex items-center justify-between">
                        <div className="space-y-1">
                            <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">AI Agents Slots</p>
                            <p className="text-xl font-black text-gray-900 leading-snug">
                                {agents ? agents.length : 0} <span className="text-gray-300 font-normal">/</span> {billingInfo.maxAgents ?? "—"}
                            </p>
                        </div>
                        <div className="px-3 py-1.5 rounded-xl bg-blue-50 text-[#83bbe1] text-[10px] font-bold uppercase tracking-wider">
                            Active Tier
                        </div>
                    </div>

                    {/* Human Agents Usage */}
                    <div className="bg-white rounded-[24px] p-6 border border-gray-100 shadow-md shadow-gray-100/30 flex items-center justify-between">
                        <div className="space-y-1">
                            <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Human Agents</p>
                            <p className="text-xl font-black text-gray-900 leading-snug">
                                {billingInfo.usedUserSlots} <span className="text-gray-300 font-normal">/</span> {billingInfo.maxUserSlots ?? "—"}
                            </p>
                        </div>
                        <div className="px-3 py-1.5 rounded-xl bg-purple-50 text-purple-500 text-[10px] font-bold uppercase tracking-wider">
                            Team Seats
                        </div>
                    </div>

                    {/* Current Subscription Plan */}
                    <div className="bg-white rounded-[24px] p-6 border border-gray-100 shadow-md shadow-gray-100/30 flex items-center justify-between">
                        <div className="space-y-1">
                            <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Subscription Plan</p>
                            <p className="text-xl font-black text-gray-900 leading-snug">
                                {billingInfo.planName}
                            </p>
                        </div>
                        {billingInfo.planName?.toLowerCase() === "enterprise" ? (
                            <div className="px-3 py-1.5 rounded-xl bg-green-50 text-green-600 text-[10px] font-bold uppercase tracking-wider">
                                Max Tier
                            </div>
                        ) : (
                            <a
                                href="/billing"
                                className="px-3 py-1.5 rounded-xl bg-gray-900 text-white hover:bg-gray-800 text-[10px] font-bold uppercase tracking-wider transition-all"
                            >
                                Upgrade
                            </a>
                        )}
                    </div>
                </div>

                <div className="bg-white rounded-[32px] border border-gray-100 shadow-xl shadow-gray-100/50 overflow-hidden animate-slideIn">
                    {isLoading ? (
                        <div className="flex flex-col items-center justify-center py-32 gap-4">
                            <div className="w-10 h-10 border-4 border-gray-100 border-t-gray-900 rounded-full animate-spin"></div>
                            <p className="text-sm font-bold text-gray-400 uppercase tracking-widest">Loading Repository...</p>
                        </div>
                    ) : !agents || agents.length === 0 ? (
                        <div className="p-16 text-center">
                            <div className="w-20 h-20 bg-gray-50 rounded-[28px] border border-gray-100 flex items-center justify-center mb-6 mx-auto">
                                <Plus size={32} className="text-gray-300" />
                            </div>
                            <h3 className="text-xs font-bold uppercase tracking-widest text-gray-900 mb-2">No Agents Found</h3>
                            <p className="text-gray-400 max-w-xs mx-auto mb-8">Create your first agent to start training your AI agents.</p>
                            <button
                                onClick={() => handleOpenModal()}
                                className="px-6 py-2.5 bg-gray-900 text-white rounded-xl font-bold text-xs uppercase tracking-widest hover:bg-gray-800 transition-all shadow-md shadow-gray-200"
                            >
                                Get Started
                            </button>
                        </div>
                    ) : (
                        <div>
                            <table className="w-full text-left">
                                <thead className="bg-gray-50/50 border-b border-gray-100">
                                    <tr>
                                        <th className="px-6 py-5 text-[10px] font-bold text-gray-400 uppercase tracking-widest">Name</th>
                                        <th className="px-6 py-5 text-[10px] font-bold text-gray-400 uppercase tracking-widest whitespace-nowrap text-center">Agent ID</th>
                                        <th className="px-6 py-5 text-[10px] font-bold text-gray-400 uppercase tracking-widest text-center">Agent Key</th>
                                        <th className="px-6 py-5 text-[10px] font-bold text-gray-400 uppercase tracking-widest text-center">Description</th>
                                        <th className="px-6 py-5 text-[10px] font-bold text-gray-400 uppercase tracking-widest text-center">Status</th>
                                        <th style={{ textAlign: 'center', paddingRight: '40px', width: '180px' }} className="pl-6 py-5 text-[10px] font-bold text-gray-400 uppercase tracking-widest">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-50">
                                    {agents.map((agent) => (
                                        <tr key={agent.id} className="hover:bg-gray-50/50 transition-all group">
                                            <td className="px-6 py-6 font-bold text-gray-900 text-sm align-middle">
                                                <div className="flex items-center gap-3">

                                                    {agent.name}
                                                </div>
                                            </td>
                                            <td className="px-6 py-6 align-middle text-center">
                                                <div className="flex items-center gap-2">
                                                    <div style={{ maxWidth: '140px' }} className="max-w-[140px] overflow-x-auto whitespace-nowrap bg-gray-50 border border-gray-100 rounded-lg px-2.5 py-1.5 custom-scrollbar flex items-center">
                                                        <code className="text-[10px] font-mono text-gray-600 tracking-wider">
                                                            {agent.id}
                                                        </code>
                                                    </div>
                                                    <button
                                                        onClick={() => {
                                                            navigator.clipboard.writeText(agent.id);
                                                            showToast("Agent ID copied to clipboard");
                                                        }}
                                                        className="p-1.5 text-gray-400 hover:text-gray-900 transition-colors flex-shrink-0 bg-gray-50 border border-gray-100 rounded-lg hover:bg-gray-100"
                                                        title="Copy Agent ID"
                                                    >
                                                        <Copy size={12} />
                                                    </button>
                                                </div>
                                            </td>
                                            <td className="px-6 py-6 align-middle text-center">
                                                <div className="flex items-center gap-2">
                                                    <div className="max-w-[150px] overflow-x-auto whitespace-nowrap bg-gray-50 border border-gray-100 rounded-lg px-2.5 py-1.5 custom-scrollbar flex items-center">
                                                        <code className="text-[10px] font-mono text-gray-600 tracking-wider">
                                                            {agent.agent_key || "GENERATING..."}
                                                        </code>
                                                    </div>
                                                    {agent.agent_key && (
                                                        <button
                                                            onClick={() => {
                                                                navigator.clipboard.writeText(agent.agent_key);
                                                                showToast("Key copied to clipboard");
                                                            }}
                                                            className="p-1.5 text-gray-400 hover:text-gray-900 transition-colors flex-shrink-0 bg-gray-50 border border-gray-100 rounded-lg hover:bg-gray-100"
                                                            title="Copy Key"
                                                        >
                                                            <Copy size={12} />
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-6 py-6 text-sm text-gray-500 max-w-[180px] break-words align-middle text-center">
                                                {agent.description || "No description provided"}
                                            </td>
                                            <td className="px-6 py-6 align-middle text-center">
                                                <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${agent.is_active
                                                    ? 'bg-blue-50 text-[#83bbe1]'
                                                    : 'bg-gray-100 text-gray-400'
                                                    }`}>
                                                    <div className={`w-1.5 h-1.5 rounded-full ${agent.is_active ? 'bg-[#83bbe1]' : 'bg-gray-400'}`}></div>
                                                    {agent.is_active ? 'Active' : 'Inactive'}
                                                </span>
                                            </td>
                                            <td style={{ textAlign: 'center', paddingRight: '40px', width: '180px' }} className="pl-6 py-6 align-middle">
                                                {agent.id === user?.agent_id || agents.length === 1 ? (
                                                    <div
                                                        className="w-12 h-12 flex items-center justify-center text-blue-500 bg-blue-50 rounded-xl shadow-sm border border-blue-100 cursor-help mx-auto"
                                                        title="Default workspace agent — protected from deletion"
                                                    >
                                                        <Shield size={22} strokeWidth={2.5} />
                                                    </div>
                                                ) : (
                                                    <button
                                                        onClick={() => handleOpenDeleteModal(agent)}
                                                        className="p-2 text-gray-400 hover:text-red-500 transition-all bg-gray-50 border border-gray-100 rounded-xl hover:bg-red-50 hover:border-red-200"
                                                        title="Delete Agent"
                                                    >
                                                        <Trash2 size={14} />
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            {/* Modal */}
            {isModalOpen && (
                <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-md flex items-center justify-center z-50 p-4 animate-fadeIn">
                    <div className="bg-white rounded-3xl w-full max-w-md overflow-hidden shadow-2xl animate-scaleIn border border-gray-100/50">
                        <div className="px-8 py-5 border-b border-gray-50 flex justify-between items-center bg-gray-50/20">
                            <div>
                                <h3 className="text-xl font-black text-gray-900 tracking-tighter uppercase">
                                    {editingAgent ? "Edit Identity" : "New Agent"}
                                </h3>
                                <p className="text-[10px] text-gray-400 font-bold uppercase tracking-[0.2em] mt-0.5">Agent Initialization</p>
                            </div>
                            <button
                                onClick={() => setIsModalOpen(false)}
                                className="p-2 text-gray-400 hover:text-gray-900 hover:bg-white rounded-xl transition-all"
                            >
                                <X size={20} />
                            </button>
                        </div>
                        <form onSubmit={handleSubmit} className="p-8 space-y-5">
                            <div className="space-y-2">
                                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">Agent Name</label>
                                <input
                                    type="text"
                                    required
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    className="w-full px-5 py-3 bg-gray-50 border-0 rounded-xl focus:ring-2 focus:ring-gray-900 text-gray-900 placeholder:text-gray-300 font-bold text-xs transition-all"
                                    placeholder="e.g. Support Agent"
                                />
                            </div>
                            <div className="space-y-2">
                                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">Internal Description</label>
                                <textarea
                                    value={formData.description}
                                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                    className="w-full px-5 py-3 bg-gray-50 border-0 rounded-xl focus:ring-2 focus:ring-gray-900 text-gray-900 placeholder:text-gray-300 text-xs transition-all resize-none font-medium"
                                    placeholder="Brief internal note regarding this agent..."
                                    rows={3}
                                />
                            </div>
                            {editingAgent && (
                                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-2xl">
                                    <div className="flex items-center gap-3">
                                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${formData.is_active ? 'bg-blue-50 text-[#83bbe1]' : 'bg-gray-100 text-gray-400'}`}>
                                            <div className="w-2 h-2 rounded-full bg-current animate-pulse"></div>
                                        </div>
                                        <div>
                                            <p className="text-xs font-bold text-gray-900">Operational Status</p>
                                            <p className="text-[10px] text-gray-500 font-medium">Currently {formData.is_active ? 'Online' : 'Offline'}</p>
                                        </div>
                                    </div>
                                    <label className="relative inline-flex items-center cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={formData.is_active}
                                            onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                                            className="sr-only peer"
                                        />
                                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#83bbe1]"></div>
                                    </label>
                                </div>
                            )}
                            <div className="flex items-center justify-center gap-3 pt-4">
                                <button
                                    type="button"
                                    onClick={() => setIsModalOpen(false)}
                                    className="px-6 py-2.5 text-gray-500 hover:text-gray-900 font-bold text-[10px] uppercase tracking-widest transition-colors rounded-xl bg-gray-50 hover:bg-gray-100"
                                >
                                    Discard
                                </button>
                                <button
                                    type="submit"
                                    className="px-10 py-2.5 bg-gray-900 text-white font-bold text-xs uppercase tracking-widest rounded-xl hover:bg-gray-800 transition-all shadow-lg shadow-gray-200 active:scale-95"
                                >
                                    {editingAgent ? "Update Agent" : "Create Agent"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            <DeleteConfirmModal
                isOpen={isDeleteModalOpen}
                onClose={() => setIsDeleteModalOpen(false)}
                onConfirm={handleDelete}
                isLoading={isLoading}
                title="Destroy Intelligence Hub?"
                message={`This action will permanently purge '${agentToDelete?.name}' and all its associated data. Are you prepared to proceed?`}
            />
        </div>
    );
};

export default Agents;
